// Page Découvrir - Exploration de contenu
// Grille de posts populaires style Instagram Explore

import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Heart, MessageCircle, Play, Grid, Hash,
  Users, TrendingUp, Sparkles, AlertCircle, Newspaper,
  Plus, Compass, Menu, X, Network, Map, Camera, Layers,
  Calendar, Award, User, Bookmark, Bell, HelpCircle
} from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import UnifiedHeader from '../../../components/layout/UnifiedHeader';
import MainNavigation from '../../../components/layout/MainNavigation';

import { API_URL } from '../../../config/api';

// Mock data for Explorer - 10 cards (5 images + 5 reels)
const MOCK_EXPLORER_POSTS = [
  // 5 IMAGES
  {
    id: 'explore-1',
    post_type: 'image',
    metier: 'Plombier',
    media: [{ url: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?w=600', type: 'image' }],
    likes_count: 234,
    comments_count: 45,
    description: 'Douche italienne encastrée carrelage grand format'
  },
  {
    id: 'explore-2',
    post_type: 'image',
    metier: 'Façadier',
    media: [{ url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600', type: 'image' }],
    likes_count: 189,
    comments_count: 32,
    description: 'Bardage bois brûlé Shou Sugi Ban finition noire'
  },
  {
    id: 'explore-3',
    post_type: 'image',
    metier: 'Vitrier',
    media: [{ url: 'https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=600', type: 'image' }],
    likes_count: 312,
    comments_count: 56,
    description: 'Verrière atelier acier noir mat intérieur'
  },
  {
    id: 'explore-4',
    post_type: 'image',
    metier: 'Charpentier',
    media: [{ url: 'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=600', type: 'image' }],
    likes_count: 267,
    comments_count: 41,
    description: 'Charpente bois massif vue intérieure cathédrale'
  },
  {
    id: 'explore-5',
    post_type: 'image',
    metier: 'Serrurier',
    media: [{ url: 'https://images.unsplash.com/photo-1558618047-f4b511985a0e?w=600', type: 'image' }],
    likes_count: 145,
    comments_count: 28,
    description: 'Portail acier forgé sur mesure entrée maison'
  },
  // 5 REELS
  {
    id: 'explore-6',
    post_type: 'reel',
    metier: 'Climaticien',
    media: [{ url: 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=600', type: 'video', thumbnail_url: 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=600' }],
    likes_count: 178,
    comments_count: 23,
    reel_duration: 5,
    description: 'Installation unité intérieure split'
  },
  {
    id: 'explore-7',
    post_type: 'reel',
    metier: 'Carreleur',
    media: [{ url: 'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=600', type: 'video', thumbnail_url: 'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=600' }],
    likes_count: 456,
    comments_count: 67,
    reel_duration: 8,
    description: 'Pose mosaïque salle de bain au ralenti'
  },
  {
    id: 'explore-8',
    post_type: 'reel',
    metier: 'Électricien',
    media: [{ url: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=600', type: 'video', thumbnail_url: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=600' }],
    likes_count: 234,
    comments_count: 38,
    reel_duration: 7,
    description: 'Câblage tableau armoire industrielle'
  },
  {
    id: 'explore-9',
    post_type: 'reel',
    metier: 'Maçon',
    media: [{ url: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600', type: 'video', thumbnail_url: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600' }],
    likes_count: 189,
    comments_count: 29,
    reel_duration: 10,
    description: 'Coulage dalle béton lissage manuel'
  },
  {
    id: 'explore-10',
    post_type: 'reel',
    metier: 'Couvreur',
    media: [{ url: 'https://images.unsplash.com/photo-1632778149955-e80f8ceca2e8?w=600', type: 'video', thumbnail_url: 'https://images.unsplash.com/photo-1632778149955-e80f8ceca2e8?w=600' }],
    likes_count: 312,
    comments_count: 48,
    reel_duration: 9,
    description: 'Pose faîtière et tuiles vue drone'
  }
];

// Trending hashtags (would come from API in real implementation)
const TRENDING_TAGS = [
  'renovation', 'plomberie', 'electricite', 'carrelage', 
  'chantier', 'artisan', 'batiment', 'travaux'
];

// Post Grid Item
function PostGridItem({ post, onClick }) {
  const isVideo = post.post_type === 'reel' || post.media?.[0]?.type === 'video';
  const imageUrl = post.media?.[0]?.url || post.media?.[0]?.thumbnail_url;
  const isTest = post.id?.startsWith('explore-');
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="aspect-square relative group cursor-pointer overflow-hidden bg-gray-100"
      onClick={() => onClick(post)}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
          <Hash size={32} className="text-gray-400" />
        </div>
      )}
      
      {/* Badge TEST */}
      {isTest && (
        <div className="absolute top-2 left-2 z-10 bg-amber-500 text-white px-2 py-0.5 rounded text-[10px] font-bold flex items-center gap-1">
          <AlertCircle size={10} />
          TEST
        </div>
      )}
      
      {/* Video/Reel indicator with duration */}
      {isVideo && (
        <div className="absolute top-2 right-2 z-10 bg-black/70 text-white px-2 py-0.5 rounded text-[10px] font-bold flex items-center gap-1">
          <Play size={10} fill="white" />
          {post.reel_duration ? `0:${post.reel_duration.toString().padStart(2, '0')}` : 'REEL'}
        </div>
      )}
      
      {/* Multiple photos indicator */}
      {post.media?.length > 1 && !isVideo && (
        <div className="absolute top-2 right-2">
          <Grid size={20} className="text-white drop-shadow-lg" />
        </div>
      )}
      
      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-6">
        <div className="flex items-center gap-1 text-white">
          <Heart size={20} fill="white" />
          <span className="font-semibold">{post.likes_count || 0}</span>
        </div>
        <div className="flex items-center gap-1 text-white">
          <MessageCircle size={20} fill="white" />
          <span className="font-semibold">{post.comments_count || 0}</span>
        </div>
      </div>
    </motion.div>
  );
}

// Search Results
function SearchResults({ results, query, onPostClick }) {
  if (!query) return null;
  
  return (
    <div className="space-y-6">
      {/* Users */}
      {results.users?.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Users size={18} />
            Utilisateurs
          </h3>
          <div className="space-y-2">
            {results.users.map((user) => (
              <Link
                key={user.id}
                to={`/reseau/profil/${user.username}`}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {user.profile_photo ? (
                  <img
                    src={user.profile_photo}
                    alt={user.display_name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-blue-600 font-semibold">
                      {user.display_name?.[0]?.toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <p className="font-medium text-gray-900">{user.display_name}</p>
                  <p className="text-sm text-gray-500">@{user.username}</p>
                </div>
                {user.is_verified && (
                  <svg className="w-4 h-4 text-blue-500 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}
      
      {/* Posts */}
      {results.posts?.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Hash size={18} />
            Publications
          </h3>
          <div className="grid grid-cols-3 gap-1">
            {results.posts.map((post) => (
              <PostGridItem key={post.id} post={post} onClick={onPostClick} />
            ))}
          </div>
        </div>
      )}
      
      {/* No results */}
      {results.users?.length === 0 && results.posts?.length === 0 && (
        <div className="text-center py-12">
          <Search size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Aucun résultat pour "{query}"</p>
        </div>
      )}
    </div>
  );
}

// Main Discover Page
export default function DiscoverPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    fetchDiscoverPosts();
  }, []);

  const fetchDiscoverPosts = async () => {
    try {
      // Always use mock data for demo
      setPosts(MOCK_EXPLORER_POSTS);
    } catch (e) {
      console.error('Error fetching posts:', e);
      setPosts(MOCK_EXPLORER_POSTS);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    
    setIsSearching(true);
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/search?q=${encodeURIComponent(searchQuery)}&limit=20`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data);
      }
    } catch (e) {
      console.error('Error searching:', e);
    } finally {
      setIsSearching(false);
    }
  };

  const handlePostClick = (post) => {
    if (post.post_type === 'reel') {
      navigate('/reseau/reels');
    } else {
      navigate(`/reseau/post/${post.id}`);
    }
  };

  const handleTagClick = (tag) => {
    setSearchQuery(`#${tag}`);
    handleSearch();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <UnifiedHeader />
        <MainNavigation />
        <div className="h-24 md:hidden" />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header Unifié */}
      <UnifiedHeader />
      
      {/* Navigation Principale */}
      <MainNavigation />

      {/* Spacer Mobile - Compense les éléments fixed */}
      <div className="h-24 md:hidden" />

      {/* Barre de recherche Explorer - Améliorée */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-2.5">
          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
            <Input
              placeholder="Rechercher des personnes, hashtags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-11 pr-4 h-12 bg-white border-2 border-gray-200 rounded-xl text-sm shadow-sm focus:border-[#1E40AF] focus:ring-2 focus:ring-[#1E40AF]/20 transition-all"
              data-testid="discover-search-input"
            />
          </form>
        </div>
      </div>

      {/* Contenu principal - Compact */}
      <div className="max-w-4xl mx-auto px-4 py-3">
        {/* Search Results */}
        {searchResults ? (
          <SearchResults 
            results={searchResults} 
            query={searchQuery}
            onPostClick={handlePostClick}
          />
        ) : (
          <>
            {/* Trending Tags - Scroll horizontal compact */}
            <div className="mb-4">
              <h2 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm">
                <TrendingUp size={16} className="text-orange-500" />
                Tendances
              </h2>
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {TRENDING_TAGS.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => handleTagClick(tag)}
                    className="flex-shrink-0 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full text-xs font-medium text-gray-700 transition-colors whitespace-nowrap"
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Explore Grid - Compact */}
            <div>
              <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                <Sparkles size={16} className="text-blue-500" />
                Explorer
              </h2>
              
              {posts.length === 0 ? (
                <div className="text-center py-12">
                  <Grid size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Aucun contenu à explorer pour le moment</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-0.5 rounded-xl overflow-hidden">
                  {posts.map((post) => (
                    <PostGridItem 
                      key={post.id} 
                      post={post} 
                      onClick={handlePostClick}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      
      {/* Barre de navigation mobile - Réseau Social */}
      <ReseauBottomNav />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT NAVIGATION MOBILE - Identique à FeedPage
// ═══════════════════════════════════════════════════════════════════════════════
function ReseauBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showMenu, setShowMenu] = useState(false);

  const isActive = (path) => {
    if (!path) return false;
    if (path === '/reseau') return location.pathname === '/reseau';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Menu Slide-in depuis la gauche */}
      <AnimatePresence>
        {showMenu && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setShowMenu(false)}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed top-0 left-0 bottom-0 w-[85%] max-w-sm bg-white z-50 flex flex-col"
            >
              {/* Header */}
              <div className="bg-[#1E40AF] px-4 py-4 flex items-center justify-between">
                <h2 className="text-white font-bold text-lg flex items-center gap-2">
                  <Network size={20} />
                  Réseau Social
                </h2>
                <button onClick={() => setShowMenu(false)} className="text-white/80 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              {/* Contenu du menu */}
              <div className="flex-1 overflow-y-auto">
                {/* Section Découvrir */}
                <div className="p-4 border-b border-gray-100">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Découvrir</h3>
                  <div className="space-y-1">
                    {[
                      { icon: Map, label: 'Carte des chantiers', path: '/reseau/chantiers', color: '#F97316' },
                      { icon: Camera, label: 'Galeries', path: '/reseau/galeries', color: '#8B5CF6' },
                      { icon: Layers, label: 'Images', path: '/reseau/images', color: '#3B82F6' },
                    ].map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.path}
                          onClick={() => { setShowMenu(false); navigate(item.path); }}
                          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${item.color}15` }}>
                            <Icon size={20} style={{ color: item.color }} />
                          </div>
                          <span className="font-medium text-gray-900">{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Section Communauté */}
                <div className="p-4 border-b border-gray-100">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Communauté</h3>
                  <div className="space-y-1">
                    {[
                      { icon: Calendar, label: 'Événements', path: '/reseau/evenements', color: '#8B5CF6' },
                      { icon: Users, label: 'Salons de discussion', path: '/reseau/salons', color: '#10B981' },
                      { icon: Award, label: 'Artisans du mois', path: '/reseau/artisans-du-mois', color: '#F59E0B' },
                    ].map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.path}
                          onClick={() => { setShowMenu(false); navigate(item.path); }}
                          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${item.color}15` }}>
                            <Icon size={20} style={{ color: item.color }} />
                          </div>
                          <span className="font-medium text-gray-900">{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Section Mon compte */}
                <div className="p-4">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Mon compte</h3>
                  <div className="space-y-1">
                    {[
                      { icon: User, label: 'Mon profil', path: '/reseau/profil', color: '#1E40AF' },
                      { icon: Bookmark, label: 'Enregistrements', path: '/reseau/enregistrements', color: '#6366F1' },
                      { icon: Bell, label: 'Notifications', path: '/reseau/notifications', color: '#F97316' },
                      { icon: Heart, label: 'Mes favoris', path: '/reseau/favoris', color: '#EC4899' },
                    ].map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.path}
                          onClick={() => { setShowMenu(false); navigate(item.path); }}
                          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${item.color}15` }}>
                            <Icon size={20} style={{ color: item.color }} />
                          </div>
                          <span className="font-medium text-gray-900">{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
